import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Step 2 of password reset.
 *
 * Two entry paths:
 *   1. User clicks the email link → URL is `/reset-password?token=...`.
 *      We submit `{ token, new_password }` straight to /auth/reset-password.
 *   2. User enters the 6-digit code → we first hit /auth/verify-reset-code
 *      to get a short-lived `code_token`, then submit `{ code_token, new_password }`.
 *
 * One page handles both so the UX stays simple. The presence of `?token=`
 * in the URL switches into "link mode" and hides the code field.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const linkToken = params.get('token');
  const initialEmail = params.get('email') || '';

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // If user lands here via the link, give a small visual cue.
  const linkMode = !!linkToken;

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => navigate('/login'), 3500);
      return () => clearTimeout(t);
    }
  }, [success, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      let body;
      if (linkMode) {
        body = { token: linkToken, new_password: password };
      } else {
        // Code path — first exchange the 6-digit code for a verified code_token,
        // then call the reset endpoint. This double-call is what enables the
        // rate limit + lockout to live in one place.
        const verifyRes = await axios.post(`${API_URL}/api/auth/verify-reset-code`, {
          email,
          code,
        });
        body = { code_token: verifyRes.data.code_token, new_password: password };
      }
      await axios.post(`${API_URL}/api/auth/reset-password`, body);
      setSuccess(true);
      toast.success('Password updated! Redirecting to sign in…');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not reset your password. Try again or request a new reset email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background luxury-mesh-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-3">
            <img src="/wemillionaires-logo.png" alt="We Masterminds" className="h-16 w-auto" />
            <h1 className="text-3xl font-playfair font-bold text-foreground tracking-wide">WE MASTERMINDS</h1>
          </Link>
          <p className="text-muted-foreground mt-2">
            {linkMode ? 'Choose a new password' : 'Enter your 6-digit code'}
          </p>
        </div>

        <div className="wm-card p-8" data-testid="reset-password-card">
          {success ? (
            <div className="text-center space-y-3">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 text-emerald-400 mx-auto">
                <Lock className="w-7 h-7" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Password updated</h2>
              <p className="text-sm text-muted-foreground">
                Sending you to the sign-in screen…
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              {!linkMode && (
                <>
                  <div>
                    <Label htmlFor="reset-email" className="wm-label">Email</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                      className="mt-2 bg-[#0a0a12] border-white/10 text-foreground rounded-md h-12 focus:border-gold focus:ring-1 focus:ring-gold/30"
                      data-testid="reset-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reset-code" className="wm-label">6-digit code</Label>
                    <Input
                      id="reset-code"
                      type="text"
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      placeholder="123456"
                      className="mt-2 bg-[#0a0a12] border-white/10 text-foreground rounded-md h-12 text-center text-2xl tracking-[0.5em] font-mono focus:border-gold focus:ring-1 focus:ring-gold/30"
                      data-testid="reset-code-input"
                    />
                    <p className="mt-2 text-xs text-muted-foreground">
                      Check the email we sent. Code expires in 15 minutes.
                    </p>
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="new-password" className="wm-label">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="At least 8 characters"
                  className="mt-2 bg-[#0a0a12] border-white/10 text-foreground rounded-md h-12 focus:border-gold focus:ring-1 focus:ring-gold/30"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password" className="wm-label">Confirm new password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  className="mt-2 bg-[#0a0a12] border-white/10 text-foreground rounded-md h-12 focus:border-gold focus:ring-1 focus:ring-gold/30"
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gold-gradient text-black hover:shadow-gold-glow font-semibold uppercase tracking-widest rounded-md"
                data-testid="reset-submit-btn"
              >
                {loading ? 'Updating…' : 'Update password'}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center space-y-2">
            {!linkMode && !success && (
              <Link
                to="/forgot-password"
                className="block text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Didn't get the email? Request a new one
              </Link>
            )}
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
